import { Select, VesselPositions } from '@/types';

export const REFERENCE_TYPES: Select<string>[] = [
  { value: 'BillOfLading', label: 'Bill of Lading' },
  { value: 'BookingNo', label: 'Booking No' },
  { value: 'CustomerRef', label: 'Customer Ref' },
];

export const DOCUMENT_TYPES = ['Manifest', 'VinList'] as const;

export type ManifestDraft = {
  manifestRef: string;
  eta?: string;
  vesselId?: number;
  vesselJourneyId?: number;
  movementId?: number;
  vesselName?: string;
  voyage?: string;
  customerId?: number;
  siteId?: number;
  notes?: string;
};

export const emptyManifestDraft = (): ManifestDraft => ({
  manifestRef: '',
  voyage: '',
  notes: '',
});

export const isManifestDraftReady = (draft: ManifestDraft) =>
  Boolean(
    draft.manifestRef.trim() &&
      draft.eta &&
      (draft.vesselId || draft.vesselName) &&
      (draft.vesselJourneyId || draft.voyage?.trim()) &&
      draft.customerId &&
      draft.siteId,
  );

export const manifestProgress = (
  eta?: string | null,
  now = Date.now(),
): 'planned' | 'inProgress' =>
  eta && new Date(eta).getTime() > now ? 'planned' : 'inProgress';

export const formatManifestWhen = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-GB', { hour12: false }) : '—';

const VIN_SERIAL_LENGTH = 6;

export const inferEndVin = (startVin?: string | null, quantity = 0) => {
  const start = startVin?.trim().toUpperCase() ?? '';
  const match = start.match(/^(.*?)(\d+)$/);
  if (!match) return '';
  const count = quantity > 0 ? quantity : 1;
  const [, prefix, digits] = match;
  const endDigits = String(Number(digits) + count - 1).padStart(
    digits.length,
    '0',
  );
  return `${prefix}${endDigits}`;
};

export const formatVinRange = (startVin?: string | null, quantity = 0) => {
  const start = startVin?.trim().toUpperCase() ?? '';
  const end = inferEndVin(start, quantity);
  if (!start || !end) return '—';
  return `${start} – ${end.slice(-VIN_SERIAL_LENGTH)}`;
};

export const formatReferenceVinRange = (
  startVin?: string | null,
  endVin?: string | null,
  quantity = 0,
) => {
  const start = startVin?.trim().toUpperCase() ?? '';
  const end = endVin?.trim().toUpperCase() ?? '';
  if (start && end && start !== end) return `${start} – ${end}`;
  if (start) return formatVinRange(start, quantity);
  return end || '—';
};

export const isVehicleRangeReady = (startVin?: string | null, quantity = 0) =>
  quantity > 0 && inferEndVin(startVin, quantity) !== '';

export const expandVinRange = (
  startVin?: string | null,
  quantity = 0,
  max = 2000,
) => {
  const start = startVin?.trim().toUpperCase() ?? '';
  const match = start.match(/^(.*?)(\d+)$/);
  if (!match || quantity < 1 || quantity > max) return [];
  const [, prefix, digits] = match;
  const serial = Number(digits);
  return Array.from(
    { length: quantity },
    (_, index) =>
      `${prefix}${String(serial + index).padStart(digits.length, '0')}`,
  );
};

export const vinRangeFromList = (vins: string[] = []) => {
  const unique = [
    ...new Set(vins.map((vin) => vin.trim().toUpperCase()).filter(Boolean)),
  ].sort((left, right) => left.localeCompare(right));
  if (!unique.length) return '—';
  if (unique.length === 1) return unique[0];
  const start = unique[0];
  const end = unique[unique.length - 1];
  return `${start} – ${end}`;
};

export const vesselSelectOptions = (vessels: VesselPositions[]) => {
  const seen = new Set<string>();
  return vessels.flatMap((vessel) => {
    const name = vessel.shipName?.trim() ?? '';
    const value = vessel.vesselId == null ? name : String(vessel.vesselId);
    if (!name || !value || seen.has(value)) return [];
    seen.add(value);
    const label = vessel.imo ? `${name} (${vessel.imo})` : name;
    return [{ value, label, vesselName: name }];
  });
};

export const portCallSelectOptions = (vessels: VesselPositions[]) =>
  uniqueOptions(
    vessels.map((vessel) =>
      [vessel.nextGbaCallCode, vessel.nextGbaCallName]
        .filter(Boolean)
        .join(' - '),
    ),
  );

const uniqueOptions = (labels: string[]) => {
  const seen = new Set<string>();
  return labels
    .map((label) => label.trim())
    .filter((label) => {
      if (!label || seen.has(label)) return false;
      seen.add(label);
      return true;
    })
    .map((label) => ({ value: label, label }));
};
